/**
 * Theological Settings Section
 * Church of God in Christ featured prominently as requested
 */
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import Select from '../common/Select';

const THEOLOGICAL_TRADITIONS = [
  { value: 'church-of-god-in-christ', label: 'Church of God in Christ (COGIC)' },
  { value: 'pentecostal', label: 'Pentecostal' },
  { value: 'holiness', label: 'Holiness' },
  { value: 'apostolic', label: 'Apostolic' },
  { value: 'wesleyan', label: 'Wesleyan' },
  { value: 'reformed', label: 'Reformed/Calvinist' },
  { value: 'arminian', label: 'Arminian' },
  { value: 'baptist', label: 'Baptist' },
  { value: 'presbyterian', label: 'Presbyterian' },
  { value: 'lutheran', label: 'Lutheran' },
  { value: 'anglican', label: 'Anglican' },
  { value: 'methodist', label: 'Methodist' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'orthodox', label: 'Orthodox' },
  { value: 'anabaptist', label: 'Anabaptist' },
  { value: 'dispensational', label: 'Dispensational' },
  { value: 'covenant', label: 'Covenant Theology' },
  { value: 'other', label: 'Other/Eclectic' },
];

const BIBLE_TRANSLATIONS = [
  { value: 'KJV', label: 'King James Version (KJV)' },
  { value: 'NKJV', label: 'New King James Version (NKJV)' },
  { value: 'ESV', label: 'English Standard Version (ESV)' },
  { value: 'NIV', label: 'New International Version (NIV)' },
  { value: 'NASB', label: 'New American Standard Bible (NASB)' },
  { value: 'CSB', label: 'Christian Standard Bible (CSB)' },
  { value: 'NLT', label: 'New Living Translation (NLT)' },
  { value: 'HCSB', label: 'Holman Christian Standard Bible (HCSB)' },
  { value: 'RSV', label: 'Revised Standard Version (RSV)' },
  { value: 'NRSV', label: 'New Revised Standard Version (NRSV)' },
  { value: 'AMP', label: 'Amplified Bible (AMP)' },
  { value: 'MSG', label: 'The Message (MSG)' },
  { value: 'NET', label: 'New English Translation (NET)' },
];

export default function TheologicalSettings() {
  const { user } = useAuthStore();
  const { updateSettings, setHasUnsavedChanges } = useSettingsStore();

  const [theologicalTradition, setTheologicalTradition] = useState(user?.theological_tradition || '');
  const [bibleTranslation, setBibleTranslation] = useState(user?.bible_translation || 'KJV');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Detect changes
  useEffect(() => {
    const hasChanges =
      theologicalTradition !== (user?.theological_tradition || '') ||
      bibleTranslation !== (user?.bible_translation || 'KJV');

    setHasUnsavedChanges(hasChanges);
  }, [theologicalTradition, bibleTranslation, user, setHasUnsavedChanges]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await updateSettings({
        theological_tradition: theologicalTradition,
        bible_translation: bibleTranslation,
      });

      setSaveMessage('Theological preferences updated successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message || 'Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">Theological Preferences</h3>
        <p className="text-sm text-dark-text-muted">
          Configure your theological tradition and preferred Bible translation to personalize Virtual Assistant responses.
        </p>
      </div>

      <div className="space-y-5">
        {/* Theological Tradition */}
        <div>
          <Select
            value={theologicalTradition}
            onChange={setTheologicalTradition}
            options={THEOLOGICAL_TRADITIONS}
            label="Theological Tradition"
            placeholder="Select your theological tradition..."
          />
          <p className="mt-2 text-xs text-dark-text-muted">
            PARACLETE will consider this tradition when providing doctrinal insights and interpretations.
          </p>
        </div>

        {/* Bible Translation */}
        <div>
          <Select
            value={bibleTranslation}
            onChange={setBibleTranslation}
            options={BIBLE_TRANSLATIONS}
            label="Preferred Bible Translation"
            placeholder="Select your preferred translation..."
          />
          <p className="mt-2 text-xs text-dark-text-muted">
            This translation will be used when quoting scripture unless otherwise specified.
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-xl p-4">
          <div className="flex gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-accent-blue flex-shrink-0 mt-0.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            <div className="text-sm text-dark-text">
              <p className="font-medium mb-1">Advanced Doctrinal Filters</p>
              <p className="text-dark-text-muted">
                For more granular control over theological positions and doctrinal boundaries, you can configure detailed filters in the Doctrinal Positions section (coming soon).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4 pt-4 border-t border-dark-border/50">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>

        {saveMessage && (
          <span className={`text-sm ${saveMessage.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
            {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
}
